import { useMemo, useState } from "react";
import {
  dossierGlobalQuality,
  aggregateLevel,
} from "@/components/dossier/dossier_quality_global";
import { LEVEL_COLORS } from "@/components/dossier/dossier_quality";

/**
 * Panel global de control de calidad del dossier.
 *
 * Vive en la toolbar superior de DossierTab. Muestra:
 *   - Total de secciones con contenido / vacías
 *   - Total de slides estimados para la presentación
 *   - Conteo de avisos por nivel (OK / info / warn / error)
 *   - Lista colapsable de TODOS los warnings de TODAS las secciones,
 *     agrupados por sección, con CTA "Ir a la sección" que hace scroll.
 *
 * Si todo está en verde, el panel se ve discreto. Si hay warnings, se
 * resalta con el color del nivel más alto detectado.
 */
export default function DossierGlobalQualityPanel({ sections }) {
  const [open, setOpen] = useState(false);
  const result = useMemo(() => dossierGlobalQuality(sections), [sections]);
  const level = useMemo(() => aggregateLevel(result.counts), [result.counts]);
  const palette = LEVEL_COLORS[level] || LEVEL_COLORS.ok;

  const totalWarnings = result.warnings.length;

  // Agrupar warnings por sección para la vista detallada
  const grouped = useMemo(() => {
    const map = new Map();
    for (const w of result.warnings) {
      if (!map.has(w.sectionKey)) {
        map.set(w.sectionKey, { label: w.sectionLabel, items: [] });
      }
      map.get(w.sectionKey).items.push(w);
    }
    return Array.from(map.entries()).map(([key, value]) => ({ key, ...value }));
  }, [result.warnings]);

  const scrollToSection = (key) => {
    const el = document.querySelector(`[data-testid="section-${key}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Pulso visual breve para guiar la atención
      el.style.transition = "box-shadow 0.4s ease-out";
      el.style.boxShadow = "0 0 0 3px rgba(148, 22, 32, 0.45)";
      setTimeout(() => { el.style.boxShadow = ""; }, 1200);
    }
  };

  return (
    <div className="w-full" data-testid="dossier-global-quality">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="dossier-global-quality-toggle"
        className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition ${palette.bg} ${palette.border} ${palette.text} hover:shadow-sm`}
      >
        <div className="flex items-center gap-3">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: palette.dot }}
          />
          <span className="text-[11px] font-semibold uppercase tracking-[0.22em]">
            Control de calidad
          </span>
          <span className="text-[12px] opacity-80">
            <strong>{result.sectionsWithContent}</strong> secciones ·
            <strong className="ml-1">{result.totalSlides}</strong> slides estimados
            {result.emptySections > 0 && (
              <span className="ml-1 text-stone-500">
                · <strong>{result.emptySections}</strong> vacías
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {totalWarnings > 0 && (
            <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold">
              {totalWarnings} aviso{totalWarnings === 1 ? "" : "s"}
            </span>
          )}
          <span className="text-stone-400">{open ? "▴" : "▾"}</span>
        </div>
      </button>

      {open && (
        <div
          className="mt-2 rounded-lg border border-stone-200 bg-white p-4 shadow-sm"
          data-testid="dossier-global-quality-panel"
        >
          {/* Stats grid */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatBox label="Secciones con contenido" value={result.sectionsWithContent} dot="#26775E" />
            <StatBox label="Slides estimados" value={result.totalSlides} dot="#941620" />
            <StatBox label="Secciones vacías" value={result.emptySections} dot="#a8a29e" muted />
            <StatBox label="Avisos totales" value={totalWarnings} dot={palette.dot} />
          </div>

          {/* Distribución de niveles */}
          {(result.counts.ok || result.counts.info || result.counts.warn || result.counts.error) > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-[11px]">
              <span className="text-stone-500 uppercase tracking-[0.18em]">Distribución:</span>
              <LevelChip level="ok" count={result.counts.ok} />
              <LevelChip level="info" count={result.counts.info} />
              <LevelChip level="warn" count={result.counts.warn} />
              <LevelChip level="error" count={result.counts.error} />
            </div>
          )}

          {/* Lista de warnings */}
          {totalWarnings === 0 ? (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-800">
              ✓ Todas las secciones con contenido están en verde. El dossier está listo para presentar.
            </p>
          ) : (
            <div className="space-y-3" data-testid="dossier-global-warnings">
              <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500">
                Avisos por sección
              </p>
              {grouped.map((g) => (
                <div
                  key={g.key}
                  className="rounded-lg border border-stone-200 bg-stone-50/60 p-3"
                  data-testid={`dossier-global-warning-${g.key}`}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="truncate font-serif text-sm font-semibold text-stone-900">
                      {g.label}
                    </p>
                    <button
                      onClick={() => scrollToSection(g.key)}
                      className="shrink-0 rounded-full border border-stone-300 px-2.5 py-0.5 text-[10px] uppercase tracking-[0.2em] text-stone-700 transition hover:border-[#941620] hover:bg-white hover:text-[#941620]"
                      data-testid={`dossier-global-jump-${g.key}`}
                    >
                      Ir →
                    </button>
                  </div>
                  <ul className="space-y-1.5">
                    {g.items.map((w, i) => {
                      const wp = LEVEL_COLORS[w.level] || LEVEL_COLORS.warn;
                      return (
                        <li
                          key={i}
                          className={`rounded border ${wp.border} ${wp.bg} px-2 py-1.5 text-[11px] ${wp.text}`}
                        >
                          {w.message}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, dot, muted = false }) {
  return (
    <div className={`rounded-lg border px-3 py-2 ${muted ? "border-stone-200 bg-stone-50" : "border-stone-200 bg-white"}`}>
      <div className="flex items-center gap-2">
        <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: dot }} />
        <span className="text-[9px] font-semibold uppercase tracking-[0.22em] text-stone-500">{label}</span>
      </div>
      <p className={`mt-1 font-serif text-2xl ${muted ? "text-stone-500" : "text-stone-900"}`}>{value}</p>
    </div>
  );
}

function LevelChip({ level, count }) {
  if (!count) return null;
  const palette = LEVEL_COLORS[level] || LEVEL_COLORS.ok;
  const labels = { ok: "OK", info: "Mejorable", warn: "Revisar", error: "Atención" };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${palette.bg} ${palette.border} ${palette.text}`}>
      <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: palette.dot }} />
      {labels[level]} · {count}
    </span>
  );
}
